import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Download, RefreshCw, Search, Trash2, X } from 'lucide-react';
import api from '../../../utils/authUtils';
import InterestPicker from '../../InterestPicker';
import { STATUSES, INTEREST_CATEGORIES } from '../../../constants/doctorProfile';

const PAGE_SIZE = 15;

const Container = styled.div`max-width:80rem;margin:0 auto;padding:2rem 1rem;`;
const BackLink = styled(Link)`display:inline-flex;align-items:center;gap:.35rem;color:#64748b;text-decoration:none;margin-bottom:1rem;`;
const Header = styled.div`display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-wrap:wrap;`;
const Title = styled.h1`font-size:1.875rem;margin:.25rem 0;display:inline-block;padding-bottom:6px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 3px;background-position:0 bottom;background-repeat:no-repeat;`;
const Subtitle = styled.p`color:#64748b;margin:0 0 1.5rem;`;
const Button = styled.button`display:inline-flex;align-items:center;gap:.45rem;border:0;padding:.6rem .9rem;background:${p=>p.$danger?'#dc2626':p.$secondary?'#64748b':'#47ABDB'};color:#fff;font-weight:700;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:${p=>p.$danger?'#b91c1c':p.$secondary?'#475569':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const KpiGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(11rem,1fr));gap:.9rem;margin-bottom:1.5rem;`;
const Kpi = styled.div`background:#fff;border:1px solid #dbe3ea;border-left:4px solid #47ABDB;padding:1rem 1.1rem;box-shadow:0 8px 24px rgba(15,23,42,.06);`;
const KpiValue = styled.div`font-size:1.9rem;font-weight:800;color:#0f172a;line-height:1;font-variant-numeric:tabular-nums;`;
const KpiLabel = styled.div`margin-top:.4rem;font-size:.82rem;font-weight:600;color:#475569;`;
const Card = styled.section`background:#fff;border:1px solid #dbe3ea;padding:1.25rem;margin-bottom:1.5rem;box-shadow:0 8px 24px rgba(15,23,42,.06);`;
const Toolbar = styled.div`display:flex;gap:.75rem;align-items:center;flex-wrap:wrap;margin-bottom:1rem;`;
const SearchBox = styled.div`position:relative;flex:1;min-width:16rem;`;
const SearchInput = styled.input`width:100%;padding:.6rem .7rem .6rem 2.1rem;border:1px solid #cbd5e1;font:inherit;&:focus{outline:none;border-color:#47ABDB;box-shadow:0 0 0 3px rgba(71,171,219,.15);}`;
const SearchIcon = styled(Search)`position:absolute;left:.6rem;top:50%;transform:translateY(-50%);color:#94a3b8;`;
const TableWrap = styled.div`overflow-x:auto;`;
const Table = styled.table`width:100%;border-collapse:collapse;font-size:.9rem;`;
const Th = styled.th`text-align:left;padding:.6rem .7rem;border-bottom:2px solid #e2e8f0;color:#475569;font-weight:700;white-space:nowrap;cursor:${p=>p.$sortable?'pointer':'default'};user-select:none;`;
const Tr = styled.tr`cursor:pointer;&:hover{background:#f8fafc;}`;
const Td = styled.td`padding:.55rem .7rem;border-bottom:1px solid #eef2f7;color:#334155;`;
const Muted = styled.span`color:#94a3b8;`;
const Pager = styled.div`display:flex;align-items:center;justify-content:flex-end;gap:.75rem;margin-top:1rem;color:#475569;font-size:.85rem;`;
const PagerBtn = styled.button`display:inline-flex;align-items:center;gap:.25rem;border:1px solid #cbd5e1;background:#fff;padding:.35rem .6rem;cursor:pointer;&:disabled{opacity:.4;cursor:not-allowed;}`;
const Empty = styled.p`color:#64748b;text-align:center;padding:2rem 0;`;
const Message = styled.div`padding:.75rem;margin-bottom:1rem;background:${p=>p.$error?'#fee2e2':'#dcfce7'};color:${p=>p.$error?'#991b1b':'#166534'};`;
const ChartCard = styled.div`background:#fff;border:1px solid #dbe3ea;padding:1.1rem;box-shadow:0 8px 24px rgba(15,23,42,.06);height:20rem;`;
const ChartTitle = styled.h3`font-size:.85rem;font-weight:700;color:#334155;margin:0 0 .75rem;`;
const ChartGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(20rem,1fr));gap:1rem;`;
const SectionTitle = styled.h2`font-size:.8rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#64748b;margin:0 0 .75rem;`;
// Detail drawer
const Overlay = styled.div`position:fixed;inset:0;background:rgba(15,23,42,.45);display:flex;justify-content:flex-end;z-index:1000;`;
const Drawer = styled.div`background:#fff;width:min(30rem,100%);height:100%;overflow-y:auto;padding:1.5rem;box-shadow:-8px 0 30px rgba(15,23,42,.2);`;
const DrawerHead = styled.div`display:flex;justify-content:space-between;align-items:flex-start;gap:1rem;margin-bottom:1rem;`;
const DrawerName = styled.h2`font-size:1.25rem;margin:0;`;
const CloseBtn = styled.button`border:0;background:transparent;color:#64748b;cursor:pointer;padding:.2rem;`;
const Group = styled.div`margin-bottom:1.25rem;`;
const GroupTitle = styled.div`font-size:.72rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#94a3b8;margin-bottom:.5rem;`;
const FieldRow = styled.div`display:flex;justify-content:space-between;gap:1rem;padding:.35rem 0;border-bottom:1px solid #f1f5f9;font-size:.88rem;`;
const FieldLabel = styled.span`color:#64748b;`;
const FieldValue = styled.span`color:#1f2937;font-weight:600;text-align:right;word-break:break-word;`;

const FilterInput = styled.input`padding:.55rem .65rem;border:1px solid #cbd5e1;font:inherit;font-size:.85rem;min-width:9rem;`;
const FilterSelect = styled.select`padding:.55rem .65rem;border:1px solid #cbd5e1;font:inherit;font-size:.85rem;min-width:9rem;`;
const ClearFiltersBtn = styled.button`border:1px solid #cbd5e1;background:#fff;color:#475569;padding:.55rem .8rem;font-size:.85rem;cursor:pointer;&:hover{background:#f1f5f9;}`;

const EditForm = styled.form`display:flex;flex-direction:column;gap:1rem;`;
const EFGroup = styled.div`display:flex;flex-direction:column;`;
const EFLabel = styled.label`margin-bottom:3px;font-weight:bold;font-size:.85rem;color:#334155;`;
const EFInput = styled.input`padding:.55rem;border:1px solid #cbd5e1;font-size:.88rem;`;
const EFTextarea = styled.textarea`padding:.55rem;border:1px solid #cbd5e1;font-size:.88rem;font-family:inherit;min-height:4.5rem;`;
const EFChecks = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.35rem;`;
const EFCheckLabel = styled.label`display:flex;gap:.4rem;align-items:flex-start;font-weight:normal;font-size:.82rem;`;
const EFSectionTitle = styled.div`font-size:.78rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#64748b;margin-top:.3rem;`;
const ButtonRow = styled.div`display:flex;gap:.6rem;align-items:center;margin-top:.5rem;`;

const fullName = u => [u.title, u.firstName, u.lastName].filter(Boolean).join(' ').trim() || '—';

const UserManagement = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  const [stats, setStats] = useState(null);
  const [openSubmissions, setOpenSubmissions] = useState(0);
  const [page, setPage] = useState(0);
  const [pageData, setPageData] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [sortBy, setSortBy] = useState('lastName');
  const [sortDir, setSortDir] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filterCountry, setFilterCountry] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInterest, setFilterInterest] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const hasFilters = Boolean(filterCountry || filterSpecialty || filterStatus || filterInterest);

  const onAuthError = useCallback(err => { if (err.response?.status === 401) navigate('/admin/login'); }, [navigate]);

  const fetchStats = useCallback(async () => {
    try { const { data } = await api.get('/api/cms/users/statistics'); setStats(data); }
    catch (err) { onAuthError(err); }
  }, [onAuthError]);

  // Mirrors Speaker Management's pending-title-change/slides banners: a
  // reminder for open items that isn't specific to any one role, so it lives
  // here in User Management rather than under Speaker Management.
  const fetchOpenSubmissions = useCallback(async () => {
    try { const { data } = await api.get('/api/cms/submissions/open-count'); setOpenSubmissions(data?.count || 0); }
    catch (err) { onAuthError(err); }
  }, [onAuthError]);

  const fetchPage = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, size: PAGE_SIZE, sortBy, sortDirection: sortDir });
      if (filterCountry) params.set('country', filterCountry);
      if (filterSpecialty) params.set('specialty', filterSpecialty);
      if (filterStatus) params.set('status', filterStatus);
      if (filterInterest) params.set('interest', filterInterest);
      const { data } = await api.get(`/api/cms/users?${params.toString()}`);
      setPageData({ content: data.content || [], totalPages: data.totalPages || 0, totalElements: data.totalElements || 0 });
    } catch (err) { onAuthError(err); setMessage({ error: true, text: t('userMgmt.loadError', 'Could not load users.') }); }
  }, [page, sortBy, sortDir, filterCountry, filterSpecialty, filterStatus, filterInterest, onAuthError]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setFilterCountry(''); setFilterSpecialty(''); setFilterStatus(''); setFilterInterest('');
    setPage(0);
  };

  const applyFilter = (setter) => (value) => { setter(value); setPage(0); };

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchOpenSubmissions(); }, [fetchOpenSubmissions]);
  useEffect(() => { fetchPage(); }, [fetchPage]);
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 3) { setSearchResults(null); return undefined; }
    const timer = setTimeout(async () => {
      try { const { data } = await api.get(`/api/cms/users/search?term=${encodeURIComponent(term)}`); setSearchResults(data || []); }
      catch (err) { onAuthError(err); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, onAuthError]);

  const searching = searchTerm.trim().length >= 3;
  const rows = searching ? (searchResults || []) : pageData.content;

  const sort = column => {
    if (searching) return;
    if (sortBy === column) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(column); setSortDir('asc'); }
    setPage(0);
  };
  const sortArrow = column => (sortBy === column && !searching ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const openDetail = async id => {
    try { const { data } = await api.get(`/api/cms/users/${id}`); setSelected(data); setEditing(false); }
    catch (err) { onAuthError(err); }
  };

  const roleLabel = status => t(`speaker.role.${status}`, status.replaceAll('_', ' '));
  const interestLabel = interest => t(`speaker.interest.${interest}`, interest);

  const openEdit = () => { setDraft(selected); setMessage(null); setEditing(true); };

  const handleEditChange = e => {
    const { name, value } = e.target;
    setDraft({ ...draft, [name]: value });
  };

  const toggleEditList = (key, value) => {
    setDraft(current => ({
      ...current,
      [key]: (current[key] || []).includes(value)
        ? current[key].filter(v => v !== value)
        : [...(current[key] || []), value],
    }));
  };

  const saveEdit = async e => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.put(`/api/cms/users/${draft.id}`, draft);
      setSelected(data);
      setEditing(false);
      setMessage({ text: t('userMgmt.saved', 'User updated.') });
      fetchPage();
      if (searching) setSearchResults(rs => (rs || []).map(u => (u.id === data.id ? { ...u, ...data } : u)));
    } catch (err) { onAuthError(err); setMessage({ error: true, text: t('userMgmt.saveFailed', 'Could not save changes.') }); }
    finally { setSaving(false); }
  };

  const removeUser = async () => {
    if (!selected) return;
    if (!window.confirm(t('userMgmt.deleteConfirm', 'Permanently delete {name}? This cannot be undone.', { name: fullName(selected) }))) return;
    setBusy(true);
    try {
      await api.delete(`/api/cms/users/${selected.id}`);
      setSelected(null);
      setMessage({ text: t('userMgmt.deleted', 'User deleted.') });
      await Promise.all([fetchPage(), fetchStats()]);
      if (searching) setSearchResults(rs => (rs || []).filter(u => u.id !== selected.id));
    } catch (err) { onAuthError(err); setMessage({ error: true, text: t('userMgmt.deleteFailed', 'Could not delete user.') }); }
    finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get('/api/cms/users/export-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', 'users.csv');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { onAuthError(err); }
  };

  const chartData = obj => Object.entries(obj || {}).map(([name, value]) => ({ name, value })).slice(0, 12);

  return (
    <Container>
      <BackLink to="/admin/dashboard"><ArrowLeft size={16} /><FormattedMessage id="userMgmt.back" defaultMessage="Back to Dashboard" /></BackLink>
      <Header>
        <Title><FormattedMessage id="userMgmt.title" defaultMessage="User Management" /></Title>
        <div style={{ display: 'flex', gap: '.6rem' }}>
          <Button $secondary onClick={() => { fetchPage(); fetchStats(); }}><RefreshCw size={16} /><FormattedMessage id="userMgmt.refresh" defaultMessage="Refresh" /></Button>
          <Button onClick={downloadCsv}><Download size={16} /><FormattedMessage id="userMgmt.exportCsv" defaultMessage="Export CSV" /></Button>
        </div>
      </Header>
      <Subtitle><FormattedMessage id="userMgmt.subtitle" defaultMessage="Browse, search and manage registered users." /></Subtitle>

      {message && <Message $error={message.error}>{message.text}</Message>}

      {openSubmissions > 0 && (
        <Card style={{ borderLeft: '4px solid #92400e', background: '#fffbeb', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#92400e" />
              <strong>
                <FormattedMessage id="userMgmt.submissions.banner"
                  defaultMessage="{count, plural, one {# scientific submission is} other {# scientific submissions are}} awaiting review."
                  values={{ count: openSubmissions }} />
              </strong>
            </div>
            <Button as={Link} to="/admin/submission-management">
              <FormattedMessage id="userMgmt.submissions.review" defaultMessage="Review submissions" />
            </Button>
          </div>
        </Card>
      )}

      <KpiGrid>
        <Kpi><KpiValue>{(stats?.totalUsers ?? pageData.totalElements ?? 0).toLocaleString(intl.locale)}</KpiValue><KpiLabel><FormattedMessage id="userMgmt.kpi.total" defaultMessage="Total users" /></KpiLabel></Kpi>
        <Kpi><KpiValue>{Object.keys(stats?.usersByCountry || {}).length}</KpiValue><KpiLabel><FormattedMessage id="userMgmt.kpi.countries" defaultMessage="Countries" /></KpiLabel></Kpi>
        <Kpi><KpiValue>{Object.keys(stats?.usersByCompany || {}).length}</KpiValue><KpiLabel><FormattedMessage id="userMgmt.kpi.institutions" defaultMessage="Institutions" /></KpiLabel></Kpi>
      </KpiGrid>

      <Card>
        <Toolbar>
          <SearchBox>
            <SearchIcon size={16} />
            <SearchInput value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={t('userMgmt.search', 'Search by name or email…')} />
          </SearchBox>
        </Toolbar>
        <Toolbar>
          <FilterInput value={filterCountry} onChange={e => applyFilter(setFilterCountry)(e.target.value)} placeholder={t('userMgmt.filter.country', 'Country contains…')} />
          <FilterInput value={filterSpecialty} onChange={e => applyFilter(setFilterSpecialty)(e.target.value)} placeholder={t('userMgmt.filter.specialty', 'Specialty contains…')} />
          <FilterSelect value={filterStatus} onChange={e => applyFilter(setFilterStatus)(e.target.value)}>
            <option value="">{t('userMgmt.filter.status', 'Any status')}</option>
            {STATUSES.map(s => <option key={s} value={s}>{roleLabel(s)}</option>)}
          </FilterSelect>
          <FilterSelect value={filterInterest} onChange={e => applyFilter(setFilterInterest)(e.target.value)}>
            <option value="">{t('userMgmt.filter.interest', 'Any topic interest')}</option>
            {INTEREST_CATEGORIES.map(category => (
              <optgroup key={category.key} label={t(category.labelId, category.labelDefault)}>
                {category.interests.map(i => <option key={i} value={i}>{interestLabel(i)}</option>)}
              </optgroup>
            ))}
          </FilterSelect>
          {hasFilters && <ClearFiltersBtn type="button" onClick={clearFilters}><FormattedMessage id="userMgmt.filter.clear" defaultMessage="Clear filters" /></ClearFiltersBtn>}
        </Toolbar>
        {searching && searchTerm.trim().length < 3 && <Muted><FormattedMessage id="userMgmt.searchHint" defaultMessage="Type at least 3 characters to search." /></Muted>}
        {searching && hasFilters && <Muted><FormattedMessage id="userMgmt.filter.searchOverride" defaultMessage="Filters are ignored while a name/email search is active." /></Muted>}
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th $sortable={!searching} onClick={() => sort('lastName')}><FormattedMessage id="userMgmt.col.name" defaultMessage="Name" />{sortArrow('lastName')}</Th>
                <Th $sortable={!searching} onClick={() => sort('email')}><FormattedMessage id="userMgmt.col.email" defaultMessage="Email" />{sortArrow('email')}</Th>
                <Th $sortable={!searching} onClick={() => sort('institution')}><FormattedMessage id="userMgmt.col.institution" defaultMessage="Institution" />{sortArrow('institution')}</Th>
                <Th $sortable={!searching} onClick={() => sort('specialty')}><FormattedMessage id="userMgmt.col.specialty" defaultMessage="Specialty" />{sortArrow('specialty')}</Th>
                <Th $sortable={!searching} onClick={() => sort('country')}><FormattedMessage id="userMgmt.col.country" defaultMessage="Country" />{sortArrow('country')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map(u => (
                <Tr key={u.id} onClick={() => openDetail(u.id)}>
                  <Td>{fullName(u)}</Td>
                  <Td>{u.email || <Muted>—</Muted>}</Td>
                  <Td>{u.institution || <Muted>—</Muted>}</Td>
                  <Td>{u.specialty || <Muted>—</Muted>}</Td>
                  <Td>{u.country || <Muted>—</Muted>}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
        {rows.length === 0 && <Empty><FormattedMessage id="userMgmt.empty" defaultMessage="No users found." /></Empty>}
        {!searching && pageData.totalPages > 1 && (
          <Pager>
            <PagerBtn disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}><ChevronLeft size={15} /><FormattedMessage id="userMgmt.prev" defaultMessage="Previous" /></PagerBtn>
            <span><FormattedMessage id="userMgmt.page" defaultMessage="Page {page} of {total}" values={{ page: page + 1, total: pageData.totalPages }} /></span>
            <PagerBtn disabled={page + 1 >= pageData.totalPages} onClick={() => setPage(p => p + 1)}><FormattedMessage id="userMgmt.next" defaultMessage="Next" /><ChevronRight size={15} /></PagerBtn>
          </Pager>
        )}
      </Card>

      {stats && (
        <>
          <SectionTitle><FormattedMessage id="userMgmt.stats" defaultMessage="Statistics" /></SectionTitle>
          <ChartGrid>
            <ChartCard>
              <ChartTitle><FormattedMessage id="userMgmt.charts.byCountry" defaultMessage="Users by country" /></ChartTitle>
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={chartData(stats.usersByCountry)}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#47ABDB" /></BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <ChartTitle><FormattedMessage id="userMgmt.charts.byInstitution" defaultMessage="Users by institution" /></ChartTitle>
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={chartData(stats.usersByCompany)}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#4e4094" /></BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartGrid>
        </>
      )}

      {selected && (
        <Overlay onClick={() => !busy && setSelected(null)}>
          <Drawer onClick={e => e.stopPropagation()}>
            <DrawerHead>
              <div>
                <DrawerName>{fullName(selected)}</DrawerName>
                <div style={{ color: '#64748b', fontSize: '.85rem' }}>{selected.email}</div>
              </div>
              <CloseBtn onClick={() => setSelected(null)} title={t('userMgmt.detail.close', 'Close')}><X size={20} /></CloseBtn>
            </DrawerHead>

            {!editing ? (
              <>
                {(selected.professionalStatuses || []).length > 0 && (
                  <Group>
                    <GroupTitle><FormattedMessage id="userMgmt.detail.roles" defaultMessage="Roles / status" /></GroupTitle>
                    {selected.professionalStatuses.map(s => roleLabel(s)).join(', ')}
                  </Group>
                )}
                {(selected.interests || []).length > 0 && (
                  <Group>
                    <GroupTitle><FormattedMessage id="userMgmt.detail.interests" defaultMessage="Topic interests" /></GroupTitle>
                    {selected.interests.map(i => interestLabel(i)).join(', ')}
                  </Group>
                )}
                <Group>
                  <GroupTitle><FormattedMessage id="userMgmt.detail.professional" defaultMessage="Professional" /></GroupTitle>
                  <Field label={t('userMgmt.field.institution', 'Institution')} value={selected.institution} />
                  <Field label={t('userMgmt.col.specialty', 'Specialty')} value={selected.specialty} />
                </Group>
                <Group>
                  <GroupTitle><FormattedMessage id="userMgmt.detail.contact" defaultMessage="Contact" /></GroupTitle>
                  <Field label={t('userMgmt.field.phone', 'Phone')} value={selected.phoneNumber} />
                  <Field label={t('userMgmt.col.email', 'Email')} value={selected.email} />
                </Group>
                <Group>
                  <GroupTitle><FormattedMessage id="userMgmt.detail.address" defaultMessage="Address" /></GroupTitle>
                  <Field label={t('userMgmt.field.street', 'Street')} value={selected.streetAddress} />
                  <Field label={t('userMgmt.field.city', 'City')} value={selected.city} />
                  <Field label={t('userMgmt.field.state', 'State / province')} value={selected.state} />
                  <Field label={t('userMgmt.field.postalCode', 'Postal code')} value={selected.postalCode} />
                  <Field label={t('userMgmt.col.country', 'Country')} value={selected.country} />
                </Group>

                <ButtonRow>
                  <Button onClick={openEdit}><FormattedMessage id="userMgmt.edit" defaultMessage="Edit" /></Button>
                  <Button $danger onClick={removeUser} disabled={busy}>
                    <Trash2 size={16} /><FormattedMessage id="userMgmt.delete" defaultMessage="Delete user" />
                  </Button>
                </ButtonRow>
              </>
            ) : (
              <EditForm onSubmit={saveEdit}>
                <EFGroup><EFLabel>{t('form.title', 'Title')}</EFLabel><EFInput name="title" value={draft.title || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('form.firstName', 'First Name')}</EFLabel><EFInput name="firstName" value={draft.firstName || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('form.lastName', 'Last Name')}</EFLabel><EFInput name="lastName" value={draft.lastName || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.field.institution', 'Institution')}</EFLabel><EFInput name="institution" value={draft.institution || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.col.specialty', 'Specialty')}</EFLabel><EFInput name="specialty" value={draft.specialty || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.field.phone', 'Phone')}</EFLabel><EFInput name="phoneNumber" value={draft.phoneNumber || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.field.street', 'Street')}</EFLabel><EFInput name="streetAddress" value={draft.streetAddress || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.field.city', 'City')}</EFLabel><EFInput name="city" value={draft.city || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.field.state', 'State / province')}</EFLabel><EFInput name="state" value={draft.state || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.field.postalCode', 'Postal code')}</EFLabel><EFInput name="postalCode" value={draft.postalCode || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('userMgmt.col.country', 'Country')}</EFLabel><EFInput name="country" value={draft.country || ''} onChange={handleEditChange} /></EFGroup>

                <EFSectionTitle><FormattedMessage id="userMgmt.detail.roles" defaultMessage="Roles / status" /></EFSectionTitle>
                <EFChecks>
                  {STATUSES.map(status => (
                    <EFCheckLabel key={status}>
                      <input type="checkbox" checked={(draft.professionalStatuses || []).includes(status)} onChange={() => toggleEditList('professionalStatuses', status)} />
                      {roleLabel(status)}
                    </EFCheckLabel>
                  ))}
                </EFChecks>

                <EFSectionTitle><FormattedMessage id="userMgmt.detail.interests" defaultMessage="Topic interests" /></EFSectionTitle>
                <InterestPicker compact value={draft.interests} onChange={v => setDraft(current => ({ ...current, interests: v }))} />

                <EFGroup><EFLabel>{t('speaker.field.shortBioEn', 'Short bio (English)')}</EFLabel><EFTextarea name="shortBioEn" value={draft.shortBioEn || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('speaker.field.shortBioDe', 'Short bio (German)')}</EFLabel><EFTextarea name="shortBioDe" value={draft.shortBioDe || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('speaker.field.photoUrl', 'Photo URL')}</EFLabel><EFInput name="photoUrl" value={draft.photoUrl || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('speaker.field.linkedin', 'LinkedIn')}</EFLabel><EFInput name="linkedInUrl" value={draft.linkedInUrl || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('speaker.field.website', 'Website')}</EFLabel><EFInput name="websiteUrl" value={draft.websiteUrl || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('speaker.field.facebook', 'Facebook / Meta')}</EFLabel><EFInput name="facebookUrl" value={draft.facebookUrl || ''} onChange={handleEditChange} /></EFGroup>
                <EFGroup><EFLabel>{t('speaker.field.instagram', 'Instagram')}</EFLabel><EFInput name="instagramUrl" value={draft.instagramUrl || ''} onChange={handleEditChange} /></EFGroup>

                <ButtonRow>
                  <Button type="submit" disabled={saving}>{saving ? t('userMgmt.saving', 'Saving…') : t('userMgmt.save', 'Save changes')}</Button>
                  <Button $secondary type="button" onClick={() => setEditing(false)} disabled={saving}><FormattedMessage id="userMgmt.cancel" defaultMessage="Cancel" /></Button>
                </ButtonRow>
              </EditForm>
            )}
          </Drawer>
        </Overlay>
      )}
    </Container>
  );
};

const Field = ({ label, value }) => (
  <FieldRow>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{value || '—'}</FieldValue>
  </FieldRow>
);

export default UserManagement;
